/**
 * EditorContatti — l'ultima schermata: i campi (dall'insieme chiuso `CAMPI_CONTATTO`, perché
 * ognuno ha il suo posto nella scheda del lead) e la casella del consenso agli aggiornamenti.
 *
 * Il consenso è SEPARATO dall'iscrizione e mai già spuntato (Garante, caso Verisure): qui si
 * scrive il testo esatto e la sua versione, che si salvano con ogni risposta come prova.
 */

import React from 'react';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, IconButton, Menu, MenuItem, Stack, Switch, Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, DeleteOutline } from '@mui/icons-material';
import {
  CAMPI_CHIAVE_LEAD, CAMPI_CONTATTO, CANALI_AGGIORNAMENTI,
  type CampoContatto, type CampoContattoId, type CanaleAggiornamenti, type SchermataContatti,
} from '@shared/questionari';
import { tokens } from '../../../theme';
import { CAMPO_CONTATTO_LABELS, CANALE_LABELS } from '../labels';
import { CampoTesto } from './CampoTesto';

interface Props {
  contatti: SchermataContatti;
  onChange: (contatti: SchermataContatti) => void;
}

/** La data di oggi come versione proposta del testo del consenso. */
function versioneDiOggi(): string {
  return new Date().toISOString().slice(0, 10);
}

export const EditorContatti: React.FC<Props> = ({ contatti, onChange }) => {
  const [menu, setMenu] = React.useState<HTMLElement | null>(null);
  const aggiorna = (patch: Partial<SchermataContatti>) => onChange({ ...contatti, ...patch });
  const campi = contatti.campi;
  const aggiornaCampo = (i: number, patch: Partial<CampoContatto>) => aggiorna({ campi: campi.map((c, x) => (x === i ? { ...c, ...patch } : c)) });
  const sposta = (i: number, verso: -1 | 1) => {
    const arrivo = i + verso;
    if (arrivo < 0 || arrivo >= campi.length) return;
    const copia = [...campi];
    [copia[i], copia[arrivo]] = [copia[arrivo], copia[i]];
    aggiorna({ campi: copia });
  };
  const disponibili = CAMPI_CONTATTO.filter((id) => !campi.some((c) => c.id === id));
  const chiaveObbligatoria = campi.some((c) => c.obbligatorio && CAMPI_CHIAVE_LEAD.includes(c.id));
  const consenso = contatti.consenso;

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <CampoTesto label="Domanda" value={contatti.domanda} onChange={(v) => aggiorna({ domanda: v })} helperText="Es. «A quale email possiamo avvisarti?»" />
        <CampoTesto label="Indicatore in alto" value={contatti.indicatore} onChange={(v) => aggiorna({ indicatore: v })} helperText="Al posto di «N di M» (es. «Ultimo passo»)" />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <CampoTesto label="Riga di aiuto" value={contatti.aiuto} onChange={(v) => aggiorna({ aiuto: v })} />
        <CampoTesto label="Bottone di invio" value={contatti.bottone} onChange={(v) => aggiorna({ bottone: v })} helperText="Es. «Iscriviti»" />
      </Stack>

      <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>Campi</Typography>
      {campi.map((c, i) => (
        <Stack key={c.id} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <Typography variant="body2" sx={{ width: 130, flexShrink: 0, fontFamily: tokens.font.mono, fontSize: '0.78rem', color: tokens.color.ash }}>
            {c.id}
          </Typography>
          <CampoTesto label="Etichetta" value={c.etichetta} onChange={(v) => aggiornaCampo(i, { etichetta: v })} />
          <CampoTesto label="Esempio nel campo" value={c.segnaposto} onChange={(v) => aggiornaCampo(i, { segnaposto: v })} />
          <CampoTesto label="Nota sotto (perché lo chiediamo)" value={c.nota} onChange={(v) => aggiornaCampo(i, { nota: v })} />
          <FormControlLabel
            sx={{ mr: 0, flexShrink: 0 }}
            control={<Checkbox size="small" checked={c.obbligatorio} onChange={(e) => aggiornaCampo(i, { obbligatorio: e.target.checked })} />}
            label={<Typography variant="caption">Obbligatorio</Typography>}
          />
          <Stack direction="row" sx={{ flexShrink: 0 }}>
            <IconButton size="small" onClick={() => sposta(i, -1)} disabled={i === 0} aria-label="Sposta su"><ArrowUpward fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => sposta(i, 1)} disabled={i === campi.length - 1} aria-label="Sposta giù"><ArrowDownward fontSize="small" /></IconButton>
            <IconButton size="small" sx={{ color: tokens.color.danger }} onClick={() => aggiorna({ campi: campi.filter((_, x) => x !== i) })} aria-label="Rimuovi campo">
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      ))}
      <Box>
        <Button size="small" startIcon={<Add />} disabled={disponibili.length === 0} onClick={(e) => setMenu(e.currentTarget)}>
          Aggiungi campo
        </Button>
        <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
          {disponibili.map((id: CampoContattoId) => (
            <MenuItem
              key={id}
              onClick={() => {
                aggiorna({ campi: [...campi, { id, etichetta: CAMPO_CONTATTO_LABELS[id], obbligatorio: false }] });
                setMenu(null);
              }}
            >
              {CAMPO_CONTATTO_LABELS[id]}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      {!chiaveObbligatoria && (
        <Alert severity="warning">
          L'email o la partita IVA devono essere obbligatorie: servono a riconoscere chi risponde (e chi torna una seconda volta).
        </Alert>
      )}

      <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: tokens.color.bone, border: `1px solid ${tokens.color.lineSoft}` }}>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={!!consenso}
              onChange={(e) => aggiorna({
                consenso: e.target.checked ? { testo: '', versione: versioneDiOggi(), canali: ['email'] } : undefined,
              })}
            />
          )}
          label={<Typography variant="body2">Casella del consenso agli aggiornamenti (facoltativa per chi compila)</Typography>}
        />
        {consenso && (
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <CampoTesto
              label="Testo del consenso"
              value={consenso.testo}
              onChange={(v) => aggiorna({ consenso: { ...consenso, testo: v } })}
              multiline
              minRows={2}
              helperText="Si salva parola per parola con ogni risposta: è la prova di che cosa la persona ha accettato"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <CampoTesto
                label="Versione del testo"
                value={consenso.versione}
                onChange={(v) => aggiorna({ consenso: { ...consenso, versione: v } })}
                helperText="Cambiala ogni volta che cambi il testo"
                sx={{ maxWidth: 220 }}
              />
              <Button size="small" onClick={() => aggiorna({ consenso: { ...consenso, versione: versioneDiOggi() } })} sx={{ mt: 0.5, flexShrink: 0 }}>
                Versione di oggi
              </Button>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Typography variant="caption" sx={{ color: tokens.color.ash }}>Canali:</Typography>
                {CANALI_AGGIORNAMENTI.map((canale: CanaleAggiornamenti) => (
                  <FormControlLabel
                    key={canale}
                    sx={{ mr: 0 }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={consenso.canali.includes(canale)}
                        onChange={(e) => aggiorna({
                          consenso: {
                            ...consenso,
                            canali: e.target.checked ? [...consenso.canali, canale] : consenso.canali.filter((x) => x !== canale),
                          },
                        })}
                      />
                    )}
                    label={<Typography variant="caption">{CANALE_LABELS[canale]}</Typography>}
                  />
                ))}
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <CampoTesto
                label="Testo del collegamento all'informativa"
                value={consenso.etichetta_informativa}
                onChange={(v) => aggiorna({ consenso: { ...consenso, etichetta_informativa: v } })}
              />
              <CampoTesto
                label="Indirizzo dell'informativa"
                value={consenso.url_informativa}
                onChange={(v) => aggiorna({ consenso: { ...consenso, url_informativa: v } })}
                placeholder="https://yumaos.com/it/privacy#lista-attesa"
              />
            </Stack>
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
